import React, { useState, useEffect, useRef } from "react";
import { getAuth, signInWithPhoneNumber, RecaptchaVerifier, ConfirmationResult } from "firebase/auth";
import { useNavigate } from "react-router-dom";
import { ReactComponent as USFlag } from "@/assets/flags/us.svg";
import { ReactComponent as CanadaFlag } from "@/assets/flags/canada.svg";
import { ReactComponent as IndiaFlag } from "@/assets/flags/india.svg";

// Country code options
const COUNTRY_OPTIONS = [
  { code: "+1", country: "US", Flag: USFlag },
  { code: "+1", country: "CA", Flag: CanadaFlag },
  { code: "+91", country: "IN", Flag: IndiaFlag },
] as const;

type CountryOption = typeof COUNTRY_OPTIONS[number];

// Define props for the component
interface PhoneAuthLoginProps {
  nextStep: () => void;
  // You might also want a prop for a "go back" or "cancel" action if this is part of a larger flow
}

const PhoneAuthLogin: React.FC<PhoneAuthLoginProps> = ({ nextStep }) => {
  // State for UI control
  const navigate = useNavigate();
  const [phoneNumber, setPhoneNumber] = useState("");
  const [verificationCode, setVerificationCode] = useState("");
  const [confirmationResult, setConfirmationResult] = useState<ConfirmationResult | null>(null);
  const [uiState, setUiState] = useState<'enterPhone' | 'enterCode'>('enterPhone');
  const [done, setDone] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [selectedCountry, setSelectedCountry] = useState<CountryOption>(COUNTRY_OPTIONS[0]);
  const [showCountryDropdown, setShowCountryDropdown] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const recaptchaVerifierRef = useRef<RecaptchaVerifier | null>(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setShowCountryDropdown(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    const auth = getAuth();
    if (!recaptchaVerifierRef.current) {
      recaptchaVerifierRef.current = new RecaptchaVerifier(auth, 'recaptcha-container', {
        'size': 'invisible', 
        'callback': (response: string) => {
        },
        'expired-callback': () => {
          console.error("reCAPTCHA expired. Please try again.");
          setError("Verification expired. Please try sending the code again.");
        }
      });
    }

    return () => {
      if (recaptchaVerifierRef.current) {
        recaptchaVerifierRef.current.clear();
        recaptchaVerifierRef.current = null;
      }
    };
  }, []);

  const handleSendCode = async () => {
    setLoading(true);
    setError(null);

    const auth = getAuth();

    try {
      let appVerifier;
    
      if (process.env.REACT_APP_USE_FIREBASE_EMULATOR === "true") {
        appVerifier = {
          type: 'recaptcha',
          verify: () => Promise.resolve('fake-token'),
          _reset: () => {} 
        } as any;
      } else {
        appVerifier = recaptchaVerifierRef.current;
        if (!appVerifier) {
          setError("Security verification (reCAPTCHA) not initialized. Please refresh the page.");
          setLoading(false);
          return;
        }
      }

      const sanitizedPhone = phoneNumber.replace(/-/g, '');
      const result = await signInWithPhoneNumber(auth, `${selectedCountry.code}${sanitizedPhone}`, appVerifier ?? undefined);
      setConfirmationResult(result); 
      setUiState('enterCode'); 
    } catch (err: unknown) {
      if (err instanceof Error) {
        setError(`Failed to send verification code: ${err.message}. Please check your phone number and try again.`);
      } else {
        setError("An unknown error occurred while sending the code.");
      }
      setUiState('enterPhone');
      setLoading(false);
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyCode = async () => {
    if (process.env.NODE_ENV === "development") {
      await verifyCodeDev();
      return;
    }
    await verifyCodeProd();
  }
  const verifyCodeProd = async () => {
    setLoading(true);
    setError(null);

    if (!confirmationResult) {
      setError("No verification process initiated. Please go back and try again.");
      setLoading(false);
      return;
    }
    try {
      const userCredential = await confirmationResult.confirm(verificationCode);
      const user = userCredential.user;


      if (user) {
        const idToken = await user.getIdToken();
        
        const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/login`, {
            method: "POST",
            credentials: "include",
            headers: {
                "Content-Type": "application/json",
            },
            body: JSON.stringify({
                firebaseToken: idToken,
                phoneNumber: `${selectedCountry.code}${phoneNumber.replace(/-/g, '')}`,
            }),
        });

        if (!response.ok) {
            const text = await response.text();
            setError(text || "Login failed on backend. Please try again.");
            setLoading(false);
            return;
        }

        const backendResponse = await response.json();
        setLoading(false);
        setDone(true); // Signal completion, which will trigger nextStep()
      } else {
          setError("Firebase sign-in did not return a user. Something went wrong.");
          setLoading(false);
      }
    } catch (err: unknown) {
        if (err instanceof Error) {
            setError(`Verification failed: ${err.message}. Please check your code or try again.`);
            // Common errors: "auth/invalid-verification-code", "auth/code-expired"
            // You might want to reset the UI to 'enterPhone' if the code expired
        } else {
            setError("An unknown error occurred during code verification.");
        }
        setLoading(false);
    }
  };

  interface VerificationCode {
    phoneNumber: string;
    sessionInfo: string;
    code: string;
  }
  
  const verifyCodeDev = async () => {
    setLoading(true);
    setError(null);

    if (!confirmationResult) {
      setError("No verification process initiated. Please go back and try again.");
      setLoading(false);
      return;
    }

    try {
      const userCredential = await confirmationResult.confirm(verificationCode);
      const user = userCredential.user;


      if (user) {
        const idToken = await user.getIdToken();

        // Call backend /login to create session
        const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/login`, {
            method: "POST",
            credentials: "include",
            headers: {
                "Content-Type": "application/json",
            },
            body: JSON.stringify({
                firebaseToken: idToken,
                phoneNumber: `${selectedCountry.code}${phoneNumber.replace(/-/g, '')}`,
            }),
        });

        if (!response.ok) {
            const text = await response.text();
            setError(text || "Login failed on backend. Please try again.");
            setLoading(false);
            return;
        }

        const backendResponse = await response.json();
        setLoading(false);
        setDone(true);
      } else {
          setError("Firebase sign-in did not return a user. Something went wrong.");
          setLoading(false);
      }
    } catch (err: unknown) {
        if (err instanceof Error) {
            setError(`Verification failed: ${err.message}. Please check your code or try again.`);
        } else {
            setError("An unknown error occurred during code verification.");
        }
        setLoading(false);
    }
  };
  

  // Effect to trigger nextStep when 'done' state becomes true
  useEffect(() => {
    if (done) {
     nextStep();
    }
  }, [done, nextStep]); // Include nextStep in dependency array for best practice

  // Inline CSS styles
  const styles = {
    container: {
      padding: '20px',
      maxWidth: '400px',
      margin: '0 auto',
      fontFamily: 'Arial, sans-serif',
      boxSizing: 'border-box' as 'border-box', // Cast for TypeScript
    },
    heading: {
      margin: '0 0 15px 0',
      textAlign: 'center' as 'center',
      fontSize: '24px',
      color: '#333',
    },
    authContainer: {
      background: '#fff',
      borderRadius: '8px',
      boxShadow: '0 2px 10px rgba(0,0,0,0.1)',
      padding: '20px',
      marginTop: '20px',
      display: 'flex',
      flexDirection: 'column' as 'column',
      gap: '15px',
    },
    textCenter: {
      textAlign: 'center' as 'center',
      marginBottom: '20px',
      color: '#666',
      fontSize: '15px',
    },
    inputGroup: {
      display: 'flex',
      alignItems: 'center',
      border: '1px solid #ddd',
      borderRadius: '4px',
      overflow: 'visible',
      position: 'relative' as 'relative',
    },
    countrySelector: {
      display: 'flex',
      alignItems: 'center',
      padding: '8px 12px',
      cursor: 'pointer',
      borderRight: '1px solid #ddd',
      backgroundColor: '#f8f8f8',
      gap: '6px',
      userSelect: 'none' as 'none',
    },
    countryFlag: {
      width: '24px',
      height: '18px',
      borderRadius: '2px',
      overflow: 'hidden',
    },
    countryCode: {
      color: '#333',
      fontSize: '14px',
      fontWeight: 500,
    },
    dropdownArrow: {
      marginLeft: '4px',
      fontSize: '10px',
      color: '#666',
    },
    countryDropdown: {
      position: 'absolute' as 'absolute',
      top: '100%',
      left: 0,
      backgroundColor: '#fff',
      border: '1px solid #ddd',
      borderRadius: '4px',
      boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
      zIndex: 100,
      marginTop: '4px',
      minWidth: '180px',
    },
    countryOption: {
      display: 'flex',
      alignItems: 'center',
      padding: '10px 12px',
      cursor: 'pointer',
      gap: '10px',
      transition: 'background-color 0.15s',
    },
    countryOptionHover: {
      backgroundColor: '#f0f0f0',
    },
    countryName: {
      color: '#333',
      fontSize: '14px',
    },
    inputField: {
      flexGrow: 1,
      padding: '10px 15px',
      paddingRight: 0,
      border: 'none',
      outline: 'none',
      fontSize: '16px',
      boxSizing: 'border-box' as 'border-box',
    },
    submitButton: {
      background: '#4CAF50',
      color: '#fff',
      border: 'none',
      padding: '10px 5px',
      cursor: 'pointer',
      fontSize: '16px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      transition: 'background-color 0.2s ease',
      borderTopRightRadius: '3px',
      borderBottomRightRadius: '3px',
    },
    disabledButton: {
      background: '#ccc',
      cursor: 'not-allowed',
    },
    svgIcon: {
      width: 20,
      height: 20, // Added height for consistency
    },
    errorText: {
      color: 'red',
      paddingTop: '10px',
      textAlign: 'center' as 'center',
      fontSize: '14px',
    },
    linkButton: {
      background: 'none',
      border: 'none',
      color: '#007BFF',
      cursor: 'pointer',
      fontSize: '14px',
      textDecoration: 'underline',
      textAlign: 'center' as 'center',
      padding: '10px 0',
    },
    secondaryButton: {
        background: 'none',
        border: '1px solid #007BFF',
        color: '#007BFF',
        padding: '10px 15px',
        borderRadius: '4px',
        cursor: 'pointer',
        fontSize: '14px',
        textAlign: 'center' as 'center',
        transition: 'background-color 0.2s ease, color 0.2s ease',
    },
    secondaryButtonHover: {
        backgroundColor: '#e6f2ff', // Light blue on hover
    }
  };

  return (
    <div style={styles.container}>
      <div style={styles.authContainer}>
        {uiState === 'enterPhone' && (
          <>
            <p style={styles.textCenter}>Enter your phone number to receive a verification code.</p>
            <div style={styles.inputGroup} ref={dropdownRef}>
              <div
                style={styles.countrySelector}
                onClick={() => setShowCountryDropdown(!showCountryDropdown)}
              >
                <selectedCountry.Flag style={styles.countryFlag} />
                <span style={styles.countryCode}>{selectedCountry.code}</span>
                <span style={styles.dropdownArrow}>&#9660;</span>
              </div>
              {showCountryDropdown && (
                <div style={styles.countryDropdown}>
                  {COUNTRY_OPTIONS.map((option, index) => (
                    <div
                      key={`${option.country}-${index}`}
                      style={styles.countryOption}
                      onClick={() => {
                        setSelectedCountry(option);
                        setShowCountryDropdown(false);
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.backgroundColor = '#f0f0f0';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.backgroundColor = 'transparent';
                      }}
                    >
                      <option.Flag style={styles.countryFlag} />
                      <span style={styles.countryName}>{option.country}</span>
                      <span style={styles.countryCode}>{option.code}</span>
                    </div>
                  ))}
                </div>
              )}
              <input
                data-cy="input-phone-num"
                type="tel"
                placeholder="Phone Number"
                value={phoneNumber}
                onChange={(e) => setPhoneNumber(e.target.value)}
                style={styles.inputField}
                disabled={loading}
              />
              <button
                data-cy="submit-btn"
                onClick={handleSendCode}
                disabled={phoneNumber.length < 10 || loading} // Basic validation for phone number length
                style={{
                  ...styles.submitButton,
                  ...(phoneNumber.length < 10 || loading ? styles.disabledButton : {}),
                }}
              >
                {loading ? 
                  <div className="loader-dots">
                    <div></div>
                    <div></div>
                    <div></div>
                  </div> : (
                  <svg style={styles.svgIcon} xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor">
                    <path fillRule="evenodd" d="M12.97 3.97a.75.75 0 0 1 1.06 0l7.5 7.5a.75.75 0 0 1 0 1.06l-7.5 7.5a.75.75 0 1 1-1.06-1.06l6.22-6.22H3a.75.75 0 0 1 0-1.5h16.19l-6.22-6.22a.75.75 0 0 1 0-1.06Z" clipRule="evenodd" />
                  </svg>
                )}
              </button>
            </div>
          </>
        )}

        {uiState === 'enterCode' && (
          <>
            <p style={styles.textCenter}>Please enter the 6-digit code sent to {phoneNumber}.</p>
            <div style={styles.inputGroup}>
              <input
                data-cy="input-verify-code"
                type="text"
                placeholder="Verification Code"
                value={verificationCode}
                onChange={(e) => setVerificationCode(e.target.value)}
                style={styles.inputField}
                disabled={loading}
                data-testid='phone-auth-login-input'
              />
              <button
                data-cy="submit-btn"
                onClick={handleVerifyCode}
                disabled={verificationCode.length !== 6 || loading} 
                style={{
                  ...styles.submitButton,
                  ...(verificationCode.length !== 6 || loading ? styles.disabledButton : {}),
                }}
              >
                {loading ? 
                  <div className="loader-dots">
                    <div></div>
                    <div></div>
                    <div></div>
                  </div> : (
                  <svg style={styles.svgIcon} xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor">
                    <path fillRule="evenodd" d="M12.97 3.97a.75.75 0 0 1 1.06 0l7.5 7.5a.75.75 0 0 1 0 1.06l-7.5 7.5a.75.75 0 1 1-1.06-1.06l6.22-6.22H3a.75.75 0 0 1 0-1.5h16.19l-6.22-6.22a.75.75 0 0 1 0-1.06Z" clipRule="evenodd" />
                  </svg>
                )}
              </button>
            </div>
            <button
                onClick={() => {
                    setUiState('enterPhone'); 
                    setConfirmationResult(null); 
                    setVerificationCode(""); 
                    setError(null); 
                }}
                disabled={loading}
                style={styles.secondaryButton}
                onMouseOver={(e) => Object.assign(e.currentTarget.style, styles.secondaryButtonHover)}
                onMouseOut={(e) => Object.assign(e.currentTarget.style, styles.secondaryButton)}
            >
                Change Phone Number or Resend Code
            </button>
          </>
        )}

        {error && <div style={styles.errorText}>{error}</div>}

        {/* reCAPTCHA container. Keep it hidden if using invisible reCAPTCHA. */}
        <div id="recaptcha-container" style={{ display: 'none' }}></div>
      </div>
    </div>
  );
};

export default PhoneAuthLogin;
